import React, { useMemo, useState, useEffect } from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { Images } from "../../../utils/Images";
import { Avatar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getRecentTransactions } from "../../../api/services/donorService";

// Helper function to format time as "X min ago"
const formatTimeAgo = (dateString) => {
  if (!dateString) return "Just now";
  const now = new Date();
  const date = new Date(dateString);
  const diffInMs = now - date;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  
  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes === 1) return "1min ago";
  if (diffInMinutes < 60) return `${diffInMinutes}min ago`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours === 1) return "1hr ago";
  if (diffInHours < 24) return `${diffInHours}hr ago`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) return "1day ago";
  return `${diffInDays}days ago`;
};

// Helper function to format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "0";
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return numAmount.toLocaleString('en-IN');
};

const LiveDonationFeed = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const home = useSelector((state) => state.cms.home);
  
  const [donationData, setDonationData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Handle donate button click - navigate to explore campaigns
  const handleDonateClick = () => {
    navigate("/campaigns"); // Navigate to explore campaigns
  };

  const cms = useMemo(() => {
    const sections = Array.isArray(home?.data) ? home.data : [];
    const section = sections.find((s) => s?.id === 66);
    return {
      title: section?.title || "",
      description: section?.description || "",
      image: section?.image || "",
    };
  }, [home]);

  // Fetch recent transactions
  useEffect(() => {
    let isMounted = true;
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        const response = await getRecentTransactions();
        console.log("Full API Response:", response);
        
        if (isMounted) {
          // Axios interceptor returns response.data, so response is already the API response body
          // Response structure: { success: true, data: [...], totalAmount: 18000, pagination: {...} }
          
          // Extract transactions array - handle different response structures
          const transactions = Array.isArray(response?.data) 
            ? response.data 
            : Array.isArray(response) 
            ? response 
            : [];
          
          console.log("Extracted transactions:", transactions);
          
          // Extract total amount
          const total = response?.totalAmount || 0;
          console.log("Total amount:", total);
          
          // Map API data to component format
          const mappedData = transactions.map((transaction) => ({
            id: transaction.id,
            name: transaction.username || "",
            time: formatTimeAgo(transaction.createdAt),
            amount: transaction.amount || 0,
            avatar: transaction.profile_picture || null,
          }));
          
          console.log("Mapped donation data:", mappedData);
          
          setDonationData(mappedData);
          setTotalAmount(total);
        }
      } catch (error) {
        console.error("Error fetching recent transactions:", error);
        if (isMounted) {
          setDonationData([]);
          setTotalAmount(0);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTransactions();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Box
      sx={{
        width: { xs: "95%", sm: "94.44%" },
        maxWidth: "1440px",
        borderRadius: { xs: "20px", sm: "34px" },
        backgroundColor: "#1D1D1B",
        minHeight: { xs: "auto", sm: "633px" },
        margin: "0 auto",
        marginTop: "40px",
        marginBottom: "15px",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        marginBottom: { xs: "20px", sm: "40px" },
      }}
    >
      {/* Image Section - Hidden on mobile, stacked on tablet */}
      {!isMobile && cms.image && (
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            order: { xs: 1, md: 1 },
            position: "relative",
          }}
        >
          <img
            src={cms.image}
            alt="Live Donation Feed"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              position: "absolute",
              borderTopLeftRadius: isTablet ? "20px" : "34px",
              borderTopRightRadius: isTablet ? "20px" : "0px",
              borderBottomLeftRadius: isTablet ? "0px" : "34px",
              top: 0,
              left: 0,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              overflow: "hidden",
            }}
          />
        </Box>
      )}

      {/* Content Section */}
      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          padding: {
            xs: "20px 16px",
            sm: "30px 24px",
            md: "46px 34px",
          },
          order: { xs: 2, md: 2 },
          position: "relative",
        }}
      >
                 <Box
           component="img"
           src={Images.whiteHeart}
           alt="White Heart"
           sx={{
             width: { xs: "20px", sm: "24px", md: "28px" },
             height: { xs: "20px", sm: "24px", md: "28px" },
             position: "absolute",
             top: { xs: "10px", sm: "10px", md: "32px" },
             right: { xs: "150px", sm: "400px", md: "215px" },
             zIndex: 1,
             display: { xs: "none", sm: "block" },
           }}
         />
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            marginBottom: { xs: "20px", sm: "30px" },
            gap: { xs: "15px", sm: "0" },
          }}
        >
          <Box
            sx={{
              // fontFamily: "Red Hat Display",
              fontWeight: { xs: 800, sm: 800 },
              fontStyle: { xs: "ExtraBold", sm: "normal" },
              fontSize: { xs: "18px", sm: "28px", md: "32px" },
              lineHeight: { xs: "100%", sm: "100%" },
              letterSpacing: { xs: "0%", sm: "0%" },
              textTransform: "capitalize",
              color: "#FFFFFF",
              verticalAlign: "middle",
            }}
          >
            {cms.title}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "7px",
              alignSelf: { xs: "flex-start", sm: "center" },
            }}
          >
            <Box
              sx={{
                width: "20px",
                height: "20px",
                backgroundColor: "#FFFFFF19",
                borderRadius: "50%",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: "9.09px",
                  height: "9.09px",
                  backgroundColor: "#F04951",
                  borderRadius: "50%",
                  zIndex: "10",
                  // Pulse animation for live indicator
                  '@keyframes livePulse': {
                    '0%': { transform: 'scale(1)', opacity: 1 },
                    '50%': { transform: 'scale(0.72)', opacity: 1 },
                    '100%': { transform: 'scale(1)', opacity: 1 },
                  },
                  animation: 'livePulse 1.2s ease-in-out infinite',
                  transformOrigin: 'center',
                }}
              />
            </Box>
            <Box
              sx={{
                // fontFamily: "Red Hat Display",
                fontWeight: { xs: 700, sm: 700 },
                fontStyle: { xs: "Bold", sm: "normal" },
                fontSize: { xs: "12px", sm: "16px" },
                lineHeight: { xs: "100%", sm: "100%" },
                letterSpacing: { xs: "0%", sm: "0%" },
                textTransform: "uppercase",
                color: "#F04951",
                verticalAlign: "middle",
              }}
            >
              Live
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            // fontFamily: "Red Hat Display",
            fontWeight: { xs: 400, sm: 400 },
            fontStyle: { xs: "Regular", sm: "normal" },
            fontSize: { xs: "12px", sm: "16px" },
            lineHeight: { xs: "19.42px", sm: "26px" },
            letterSpacing: { xs: "0%", sm: "0%" },
            textTransform: "capitalize",
            color: "#FFFFFFCC",
            marginBottom: { xs: "20px", sm: "30px" },
            verticalAlign: "middle",
          }}
        >
          {cms.description}
        </Box>

        <Box
          sx={{
            padding: { xs: "12px 12px", sm: "15px 16px" },
            backgroundColor: "#FFFFFF0F",
            borderRadius: "10px",
            marginBottom: { xs: "30px", sm: "40px" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", sm: "row" },
              justifyContent: { xs: "space-between", sm: "space-between" },
              alignItems: { xs: "center", sm: "center" },
              gap: { xs: "0", sm: "0" },
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "row", sm: "column" },
                alignItems: { xs: "center", sm: "flex-start" },
                gap: { xs: "16px", sm: "0" },
                flex: { xs: 1, sm: "none" },
                justifyContent: { xs: "space-between", sm: "flex-start" },
              }}
            >
              <Box
                sx={{
                  // fontFamily: "Red Hat Display",
                  fontWeight: { xs: 500, sm: 500 },
                  fontStyle: { xs: "Medium", sm: "normal" },
                  fontSize: { xs: "12px", sm: "14px" },
                  lineHeight: { xs: "12px", sm: "16.8px" },
                  letterSpacing: { xs: "0%", sm: "0%" },
                  color: "#FFFFFF",
                  marginBottom: { xs: "0px", sm: "6px" },
                  verticalAlign: "middle",
                }}
              >
                Raised in the past hour
              </Box>
              <Box
                sx={{
                  // fontFamily: "Red Hat Display",
                  fontWeight: { xs: 700, sm: 700 },
                  fontStyle: { xs: "Bold", sm: "normal" },
                  fontSize: { xs: "16px", sm: "20px" },
                  lineHeight: { xs: "100%", sm: "24px" },
                  letterSpacing: { xs: "0%", sm: "0%" },
                  color: "#A9C74E",
                  verticalAlign: "middle",
                }}
              >
                ₹{formatCurrency(totalAmount)}
              </Box>
            </Box>
            {/* Hide donate button on mobile */}
            {!isMobile && (
              <Box
              onClick={handleDonateClick}
                sx={{
                  backgroundColor: "#F04951",
                  color: "#FFFFFF",
                  padding: { xs: "12px 20px", sm: "15px 24px" },
                  borderRadius: "10px",
                  fontWeight: 600,
                  fontStyle: "normal",
                  fontSize: "14px",
                  lineHeight: "14px",
                  letterSpacing: "0%",
                  textTransform: "capitalize",
                  cursor: "pointer",
                  alignSelf: { xs: "stretch", sm: "auto" },
                  textAlign: "center",
                }}
              >
                Donate Now
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            border: "1px solid #4A4A49",
            borderRadius: "16px",
            padding: { xs: "16px 12px", sm: "20px 16px" },
            minHeight: { xs: "auto", sm: "300px" },
          }}
        >
          <Box
            sx={{
              maxHeight: { xs: "210px", sm: "260px" },
              overflowY: "auto",

              // Hide scrollbar for IE/Edge
              msOverflowStyle: "none",

              // Firefox scrollbar styling
              scrollbarWidth: "thin",
              scrollbarColor: "#F04951 #4A4A49",

              // WebKit scrollbar styling (Chrome, Safari, Edge)
              "&::-webkit-scrollbar": {
                width: "9px",
                backgroundColor: "#4A4A49",
                borderRadius: "10px", // Border radius for scrollbar background
              },
              "&::-webkit-scrollbar-track": {
                background: "#4A4A49",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#F04951",
                borderRadius: "10px",
              },
              // Hide all scrollbar buttons/arrows (more comprehensive)
              "&::-webkit-scrollbar-button": {
                display: "none",
                width: "0",
                height: "0",
              },
              "&::-webkit-scrollbar-button:start:decrement": {
                display: "none",
              },
              "&::-webkit-scrollbar-button:end:increment": {
                display: "none",
              },
              // Additional arrow hiding for different browsers
              "&::-webkit-scrollbar-corner": {
                background: "transparent",
              },
            }}
          >
            {donationData.map((donation, index) => (
              <Box
                key={donation.id}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginRight: { xs: "12px", sm: "22px" },
                  paddingTop: index === 0 ? "0px" : { xs: "10px", sm: "18px" },
                  paddingBottom: { xs: "10px", sm: "18px" },
                  borderBottom:
                    index < donationData.length - 1
                      ? "1px solid #4A4A49"
                      : "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: "10px", sm: "12px" },
                    flex: 1,
                  }}
                >
                  <Avatar
                    src={donation.avatar || null}
                    sx={{
                      width: { xs: "32px", sm: "36px" },
                      height: { xs: "32px", sm: "36px" },
                      backgroundColor: "#4A4A49",
                    }}
                  >
                    {donation.name ? donation.name.charAt(0).toUpperCase() : ""}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      sx={{
                        // fontFamily: "Red Hat Display",
                        fontWeight: { xs: 700, sm: 700 },
                        fontStyle: { xs: "Bold", sm: "Bold" },
                        fontSize: { xs: "14px", sm: "16px" },
                        lineHeight: { xs: "100%", sm: "100%" },
                        letterSpacing: { xs: "0%", sm: "0%" },
                        verticalAlign: "middle",
                        color: "#FFFFFF",
                        marginBottom: "6px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {donation.name}
                    </Box>
                    <Box
                      sx={{
                        // fontFamily: "Red Hat Display",
                        fontWeight: { xs: 500, sm: 500 },
                        fontStyle: { xs: "Medium", sm: "Medium" },
                        fontSize: { xs: "12px", sm: "14px" },
                        lineHeight: { xs: "100%", sm: "100%" },
                        letterSpacing: { xs: "0%", sm: "0%" },
                        verticalAlign: "middle",
                        color: "#FFFFFFCC",
                      }}
                    >
                      {donation.time}
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-end", sm: "center" },
                    textAlign: "right",
                    flexShrink: 0,
                    marginLeft: { xs: "8px", sm: "12px" },
                    gap: { xs: "2px", sm: "0" },
                  }}
                >
                  <Box
                    sx={{
                      // fontFamily: "Red Hat Display",
                      fontWeight: { xs: 400, sm: 700 },
                      fontStyle: { xs: "Regular", sm: "Bold" },
                      fontSize: { xs: "12px", sm: "16px" },
                      lineHeight: { xs: "130%", sm: "100%" },
                      letterSpacing: { xs: "0%", sm: "0%" },
                      verticalAlign: "middle",
                      color: { xs: "#979796", sm: "#FFFFFF" },
                      textAlign: "right",
                    }}
                  >
                    Donated
                  </Box>
                  {/* Show dash only on tablet/desktop */}
                  {!isMobile && (
                    <Box
                      sx={{
                        // fontFamily: "Red Hat Display",
                        fontWeight: 700,
                        fontStyle: "Bold",
                        fontSize: "16px",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                        verticalAlign: "middle",
                        color: "#FFFFFF",
                        textAlign: "right",
                        margin: "0 8px",
                      }}
                    >
                      -
                    </Box>
                  )}
                  <Box
                    sx={{
                      // fontFamily: "Red Hat Display",
                      fontWeight: { xs: 700, sm: 700 },
                      fontStyle: { xs: "Bold", sm: "Bold" },
                      fontSize: { xs: "14px", sm: "16px" },
                      lineHeight: { xs: "130%", sm: "100%" },
                      letterSpacing: { xs: "0%", sm: "0%" },
                      verticalAlign: "middle",
                      color: "#FFFFFF",
                      textAlign: "right",
                    }}
                  >
                    ₹{formatCurrency(donation.amount)}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LiveDonationFeed;
